<template>
	<div class="join-us-container">
		<!-- Hero Section -->
		<section class="recruitment-section">
			<div class="recruitment-intro">
				<h2 class="section-title">{{ $t('recruitmentTitle') }}</h2>
				<p class="intro-text">{{ $t('recruitmentIntro') }}</p>
			</div>
		</section>
		<!-- Main Content -->
		<div class="main-content">
			<!-- Filters Section -->
			<section class="filters-section">
				<div class="results-header">
					<h2>{{ $t('joinUs.filterTitle') }}</h2>
					<p class="results-count">{{ filteredJobs.length }} {{ $t('joinUs.positionsAvailable') }}</p>
				</div>
				<div class="filter-grid">
					<div class="filter-group">
						<label for="department">{{ $t('joinUs.department') }}</label>
						<select id="department" v-model="tempFilters.department" class="filter-select">
							<option value="">{{ $t('joinUs.allDepartments') }}</option>
							<option value="销售部">{{ $t('joinUs.sales') }}</option>
							<option value="客服部">{{ $t('joinUs.service') }}</option>
							<option value="市场部">{{ $t('joinUs.marketing') }}</option>
							<option value="航线部">{{ $t('joinUs.tech') }}</option>
						</select>
					</div>

					<div class="filter-group">
						<label for="location">{{ $t('joinUs.location') }}</label>
						<select id="location" v-model="tempFilters.location" class="filter-select">
							<option value="">{{ $t('joinUs.allLocations') }}</option>
							<option value="北京">{{ $t('joinUs.beijing') }}</option>
							<option value="上海">{{ $t('joinUs.shanghai') }}</option>
							<option value="广州">{{ $t('joinUs.guangzhou') }}</option>
							<option value="郑州">{{ $t('joinUs.zhengzhou') }}</option>
							<option value="成都">{{ $t('joinUs.chengdu') }}</option>
						</select>
					</div>

					<div class="filter-group">
						<label for="jobType">{{ $t('joinUs.jobType') }}</label>
						<select id="jobType" v-model="tempFilters.jobType" class="filter-select">
							<option value="">{{ $t('joinUs.allJobTypes') }}</option>
							<option value="全职">{{ $t('joinUs.fullTime') }}</option>
						</select>
					</div>

					<div class="filter-actions">
						<button class="search-btn" @click="applyFilters">
							<i class="fas fa-search"></i> {{ $t('joinUs.search') }}
						</button>
						<button class="reset-filters" @click="resetFilters">
							<i class="fas fa-undo-alt"></i> {{ $t('joinUs.resetFilters') }}
						</button>
					</div>
				</div>
			</section>

			<!-- Job Listings -->
			<section class="jobs-section">
				<div class="results-header">
					<h2>{{ $t('joinUs.openPositions') }}</h2>
					<!-- <p class="results-count">{{ filteredJobs.length }} {{ $t('joinUs.positionsAvailable') }}</p> -->
				</div>

				<div class="job-list">
					<article class="job-card" v-for="job in filteredJobs" :key="job.id" @click="selectedJob = job">
						<div class="job-card-content">
							<h3 class="job-title">{{ job.title }}</h3>
							<div class="job-meta">
								<span class="job-department">
									<i class="icon-department">📋</i> {{ job.department }}
								</span>
								<span class="job-location">
									<i class="icon-location">📍</i> {{ job.location }}
								</span>
							</div>
							<span class="job-type" :class="getJobTypeClass(job.type)">
								{{ job.type }}
							</span>
						</div>
						<button class="view-details-btn">
							{{ $t('joinUs.viewDetails') }}
						</button>
					</article>

					<div v-if="filteredJobs.length === 0" class="no-results">
						<p>{{ $t('joinUs.noJobsFound') }}</p>
						<button @click="resetFilters" class="reset-btn">
							{{ $t('joinUs.resetFilters') }}
						</button>
					</div>
				</div>
			</section>

			<!-- Benefits Section -->
			<section class="benefits-section">
				<h2>{{ $t('joinUs.benefits') }}</h2>
				<div class="benefits-grid">
					<div class="benefit-card" v-for="(benefit, index) in $t('joinUs.benefitList')" :key="index">
						<div class="benefit-icon">
							<i :class="getBenefitIcon(index)">{{ ['❤️', '📚', '⛱️', '👥'][index % 4] }}</i>
						</div>
						<h3>{{ $t(`joinUs.benefitTitles[${index}]`) }}</h3>
						<p>{{ benefit }}</p>
					</div>
				</div>
			</section>
		</div>

		<!-- Job Detail Modal -->
		<transition name="modal">
			<div class="job-modal" v-if="selectedJob">
				<div class="modal-overlay" @click="selectedJob = null"></div>
				<div class="modal-container">
					<div class="modal-header">
						<h2>{{ selectedJob.title }}</h2>
						<button class="modal-close" @click="selectedJob = null">
							✕
						</button>
					</div>

					<div class="modal-body">
						<div class="job-details">
							<div class="detail-item">
								<i>📋</i>
								<span>{{ $t('joinUs.departmentLabel') }} {{ selectedJob.department }}</span>
							</div>
							<div class="detail-item">
								<i>📍</i>
								<span>{{ $t('joinUs.locationLabel') }} {{ selectedJob.location }}</span>
							</div>
							<div class="detail-item">
								<i>⏱️</i>
								<span>{{ $t('joinUs.typeLabel') }} {{ selectedJob.type }}</span>
							</div>
						</div>

						<div class="job-description" v-html="selectedJob.description"></div>

						<!-- Application Form -->
						<div class="apply-email">
							{{ $t('joinUs.sendResume') }}：
							<a href="mailto:haosijin@fst.net.cn">haosijin@fst.net.cn</a>
						</div>
					</div>
				</div>
			</div>
		</transition>
	</div>
</template>

<script>
	export default {
		data() {
			return {
				tempFilters: { // 临时存储用户选择的过滤条件
					department: '',
					location: '',
					jobType: ''
				},
				filters: { // 实际应用的过滤条件
					department: '',
					location: '',
					jobType: ''
				},
				selectedJob: null,
				application: {
					name: '',
					email: '',
					phone: '',
					wechat: '',
					resume: null,
					coverLetter: ''
				},
				isApplying: false,
				cultureValues: [{
					id: 1,
					icon: "fas fa-users",
					title: "团队合作",
					description: "我们相信团队的力量，鼓励开放沟通和协作",
					color: "#4a89dc"
				}],
			};
		},
		computed: {
			filteredJobs() {
				const jobs = this.$t('joinUs.jobs');

				// i18n 可能会返回对象类型（不是响应式数组），需确认是数组
				if (!Array.isArray(jobs)) return [];

				return jobs.filter(job => {
					return (
						(!this.filters.department || job.department === this.filters.department) &&
						(!this.filters.location || job.location === this.filters.location) &&
						(!this.filters.jobType || job.type === this.filters.jobType)
					);
				});
			}
		},
		methods: {
			getJobTypeClass(type) {
				return {
					'full-time': type === '全职',
					'part-time': type === '兼职',
					'internship': type === '实习'
				};
			},
			getBenefitIcon(index) {
				return ['health', 'learning', 'vacation', 'team'][index % 4];
			},
			handleResumeUpload(event) {
				const file = event.target.files[0];
				const allowedTypes = ['application/pdf', 'application/msword',
					'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
				];
				const maxSize = 5 * 1024 * 1024; // 5MB

				if (!file) return;

				if (!allowedTypes.includes(file.type)) {
					alert(this.$t('joinUs.invalidFileType'));
					return;
				}

				if (file.size > maxSize) {
					alert(this.$t('joinUs.fileTooLarge'));
					return;
				}

				this.application.resume = file;
			},
			applyFilters() {
				// 将临时过滤条件应用到实际过滤条件
				this.filters = {
					department: this.tempFilters.department,
					location: this.tempFilters.location,
					jobType: this.tempFilters.jobType
				};
			},
			resetFilters() {
				this.tempFilters = {
					department: '',
					location: '',
					jobType: ''
				};
				this.filters = {
					department: '',
					location: '',
					jobType: ''
				};
			},
			submitApplication() {
				this.isApplying = true;
				// Simulate API request
				setTimeout(() => {
					alert(this.$t('joinUs.applicationSuccessMessage'));
					this.isApplying = false;
					this.resetApplication();
					this.selectedJob = null;
				}, 1500);
			},
			resetApplication() {
				this.application = {
					name: '',
					email: '',
					phone: '',
					wechat: '',
					resume: null,
					coverLetter: ''
				};
			}
		},
		watch: {
			selectedJob(val) {
				if (val) {
					document.body.style.overflow = 'hidden';
					this.resetApplication();
				} else {
					document.body.style.overflow = '';
				}
			}
		}
	};
</script>

<style scoped lang="scss">
	/* 变量定义 */
	:root {
		--primary-color: #4a7dff;
		--primary-hover: #3a6def;
		--danger-color: #ff4757;
		--danger-hover: #ff0000;
		--success-color: #2ecc71;
		--text-color: #333;
		--light-gray: #f5f5f5;
		--border-radius: 6px;
		--box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
		--transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
	}

	/* 按钮基础样式 */
	.btn {
		padding: 12px 24px;
		border: none;
		border-radius: var(--border-radius);
		cursor: pointer;
		font-weight: 600;
		display: inline-flex;
		align-items: center;
		gap: 8px;
		transition: var(--transition);
		position: relative;
		overflow: hidden;
		text-align: center;
		justify-content: center;
	}

	.btn::after {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0));
		opacity: 0;
		transition: opacity 0.3s;
	}

	.btn:hover {
		transform: translateY(-2px);
	}

	.btn:hover::after {
		opacity: 1;
	}

	.btn:active {
		transform: translateY(0);
	}

	.btn i {
		font-size: 14px;
		transition: transform 0.3s ease;
	}

	/* 搜索按钮样式 */
	.search-btn {
		@extend .btn;
		background: #4a6bff;
		border-radius: 25px;
		color: white;
		box-shadow: 0 4px 6px rgba(74, 125, 255, 0.2);
	}

	.search-btn:hover {
		box-shadow: 0 6px 12px rgba(74, 125, 255, 0.3);
	}

	.search-btn:active {
		box-shadow: 0 2px 4px rgba(74, 125, 255, 0.2);
	}

	.search-btn:hover i {
		transform: scale(1.1);
	}

	/* 重置按钮样式 */
	.reset-filters {
		@extend .btn;
		background: white;
		color: var(--danger-color);
		border-radius: 25px;
		border: 2px solid var(--danger-color);
		box-shadow: 0 4px 6px rgba(255, 71, 87, 0.1);
	}

	.reset-filters:hover {
		background: #fff5f5;
		box-shadow: 0 6px 12px rgba(255, 71, 87, 0.15);
		color: var(--danger-hover);
		border-color: var(--danger-hover);
	}

	.reset-filters:active {
		box-shadow: 0 2px 4px rgba(255, 71, 87, 0.1);
	}

	.reset-filters:hover i {
		animation: spin 0.5s ease;
	}

	@keyframes spin {
		0% {
			transform: rotate(0deg);
		}

		100% {
			transform: rotate(-360deg);
		}
	}

	/* 查看详情按钮样式 */
	.view-details-btn {
		@extend .btn;
		margin-top: 20px;
		padding: 10px 20px;
		background: #4a6bff;
		border-radius: 25px;
		color: white;
		font-weight: 500;
		align-self: flex-start;
	}

	.view-details-btn:hover {
		box-shadow: 0 4px 8px rgba(74, 125, 255, 0.3);
	}

	/* 无结果时的重置按钮 */
	.reset-btn {
		@extend .btn;
		padding: 10px 20px;
		background: linear-gradient(135deg, var(--primary-color), #6a5acd);
		color: white;
		font-weight: 500;
		box-shadow: 0 2px 4px rgba(74, 125, 255, 0.2);
	}

	.reset-btn:hover {
		box-shadow: 0 4px 8px rgba(74, 125, 255, 0.3);
	}

	/* 按钮容器布局 */
	.filter-actions {
		display: flex;
		gap: 15px;
		align-items: flex-end;
	}

	/* 其他原有样式保持不变... */
	.recruitment-section {
		padding-top: 60px;

		.recruitment-intro {
			text-align: center;
			max-width: 800px;
			margin: 0 auto 40px;

			.intro-text {
				font-size: 1.1rem;
				line-height: 1.8;
				color: #666;
			}
		}

		.culture-values {
			display: grid;
			grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
			gap: 30px;
			margin-top: 40px;

			.value-card {
				text-align: center;
				padding: 30px 20px;
				background: white;
				border-radius: 8px;
				box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
				transition: all 0.3s ease;

				&:hover {
					transform: translateY(-5px);
					box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
				}

				.value-icon {
					width: 60px;
					height: 60px;
					border-radius: 50%;
					display: flex;
					align-items: center;
					justify-content: center;
					margin: 0 auto 20px;
					font-size: 1.5rem;
					color: white;
				}

				h3 {
					font-size: 1.2rem;
					margin-bottom: 15px;
					color: #2c3e50;
				}

				p {
					font-size: 0.9rem;
					color: #666;
				}
			}
		}
	}

	.join-us-container {
		max-width: 1200px;
		margin: 0 auto;
		padding: 0 20px;
		font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
		color: #333;
	}

	.hero-section {
		background: linear-gradient(135deg, #4a6bff, #6a11cb);
		color: white;
		padding: 60px 20px;
		text-align: center;
		border-radius: 8px;
		margin-bottom: 40px;
	}

	.hero-section h1 {
		font-size: 2.5rem;
		margin-bottom: 15px;
	}

	.hero-section .subtitle {
		font-size: 1.2rem;
		opacity: 0.9;
		max-width: 700px;
		margin: 0 auto;
	}

	.main-content {
		border-radius: 8px;
		box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
		padding: 30px;
		// margin-bottom: 40px;
	}

	.filters-section {
		margin-bottom: 40px;
	}

	.filters-section h2 {
		font-size: 1.5rem;
		margin-bottom: 20px;
		color: #4a6bff;
	}

	.filter-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
		gap: 20px;
		align-items: end;
	}

	.filter-group {
		display: flex;
		flex-direction: column;
	}

	.filter-group label {
		font-weight: 600;
		margin-bottom: 8px;
		color: #666;
	}

	.filter-select {
		padding: 10px 15px;
		border: 1px solid #ddd;
		border-radius: 4px;
		background: white;
		font-size: 1rem;
		transition: border-color 0.3s;
	}

	.filter-select:focus {
		outline: none;
		border-color: #4a6bff;
	}

	.jobs-section {
		margin-bottom: 50px;
	}

	.results-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 20px;
	}

	.results-header h2 {
		font-size: 1.5rem;
		color: #4a6bff;
	}

	.results-header .results-count {
		color: #666;
	}

	.job-list {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
		gap: 20px;
	}

	.job-card {
		border: 1px solid #ddd;
		border-radius: 8px;
		padding: 20px;
		display: flex;
		flex-direction: column;
		transition: transform 0.3s, box-shadow 0.3s;
		cursor: pointer;
	}

	.job-card:hover {
		transform: translateY(-5px);
		box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
	}

	.job-card-content {
		flex: 1;
	}

	.job-title {
		font-size: 1.2rem;
		margin-bottom: 10px;
		color: #4a6bff;
	}

	.job-meta {
		display: flex;
		flex-wrap: wrap;
		gap: 15px;
		margin-bottom: 15px;
		color: #666;
		font-size: 0.9rem;
	}

	.job-meta span {
		display: flex;
		align-items: center;
	}

	.job-meta i {
		margin-right: 5px;
		color: #4a6bff;
	}

	.job-type {
		display: inline-block;
		padding: 4px 10px;
		border-radius: 12px;
		font-size: 0.8rem;
		font-weight: 600;
	}

	.job-type.full-time {
		background: rgba(76, 175, 80, 0.1);
		color: #4CAF50;
	}

	.job-type.part-time {
		background: rgba(255, 152, 0, 0.1);
		color: #FF9800;
	}

	.job-type.internship {
		background: rgba(33, 150, 243, 0.1);
		color: #2196F3;
	}

	.no-results {
		grid-column: 1 / -1;
		text-align: center;
		padding: 40px 0;
	}

	.no-results p {
		font-size: 1.1rem;
		margin-bottom: 20px;
		color: #666;
	}

	.benefits-section {
		margin-bottom: 50px;
	}

	.benefits-section h2 {
		font-size: 1.5rem;
		margin-bottom: 30px;
		color: #4a6bff;
		text-align: center;
	}

	.benefits-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
		gap: 30px;
	}

	.benefit-card {
		text-align: center;
		padding: 25px 20px;
		border-radius: 8px;
		background: #f9f9f9;
		transition: transform 0.3s;
	}

	.benefit-card:hover {
		transform: translateY(-5px);
	}

	.benefit-icon {
		width: 60px;
		height: 60px;
		margin: 0 auto 20px;
		background: #4a6bff;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		color: white;
		font-size: 1.5rem;
	}

	.benefit-card h3 {
		font-size: 1.1rem;
		margin-bottom: 15px;
		color: #4a6bff;
	}

	.benefit-card p {
		color: #666;
		line-height: 1.6;
	}

	.testimonial-card {
		background: white;
		border-radius: 8px;
		padding: 25px;
		box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
	}

	.testimonial-content {
		display: flex;
		flex-direction: column;
		height: 100%;
	}

	.testimonial-message {
		font-style: italic;
		line-height: 1.6;
		margin-bottom: 20px;
		flex: 1;
	}

	.testimonial-author {
		display: flex;
		align-items: center;
	}

	.author-avatar {
		width: 50px;
		height: 50px;
		border-radius: 50%;
		background: #4a6bff;
		color: white;
		display: flex;
		align-items: center;
		justify-content: center;
		font-weight: bold;
		margin-right: 15px;
	}

	.author-name {
		font-weight: 600;
		margin-bottom: 3px;
	}

	.author-position {
		font-size: 0.9rem;
		color: #666;
	}

	.job-modal {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		z-index: 1000;
		display: flex;
		justify-content: center;
		align-items: center;
	}

	.modal-overlay {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: rgba(0, 0, 0, 0.5);
	}

	.modal-container {
		position: relative;
		background: white;
		border-radius: 8px;
		width: 90%;
		max-width: 800px;
		max-height: 90vh;
		overflow-y: auto;
		box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
		z-index: 1001;
	}

	.modal-header {
		padding: 20px;
		border-bottom: 1px solid #ddd;
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	.modal-header h2 {
		font-size: 1.5rem;
		color: #4a6bff;
		margin: 0;
	}

	.modal-close {
		background: none;
		border: none;
		font-size: 1.5rem;
		cursor: pointer;
		color: #666;
		transition: color 0.3s;
	}

	.modal-close:hover {
		color: #4a6bff;
	}

	.modal-body {
		padding: 20px;
	}

	.job-details {
		display: flex;
		flex-wrap: wrap;
		gap: 20px;
		margin-bottom: 30px;
		padding-bottom: 20px;
		border-bottom: 1px solid #ddd;
	}

	.detail-item {
		display: flex;
		align-items: center;
		color: #666;
	}

	.detail-item i {
		margin-right: 8px;
		color: #4a6bff;
	}

	.job-description {
		margin-bottom: 30px;
		line-height: 1.6;
	}

	.job-description h3 {
		font-size: 1.2rem;
		color: #4a6bff;
		margin: 20px 0 10px;
	}

	.job-description ul {
		padding-left: 20px;
		margin-bottom: 15px;
	}

	.job-description li {
		margin-bottom: 8px;
	}

	.application-form {
		background: #f9f9f9;
		padding: 25px;
		border-radius: 8px;
	}

	.application-form h3 {
		font-size: 1.2rem;
		margin-bottom: 20px;
		color: #4a6bff;
	}

	.apply-email {
		margin-top: 20px;
		font-size: 16px;
		color: #333;
		background-color: #f9f9f9;
		padding: 12px 16px;
		border-left: 4px solid #409EFF;
		border-radius: 4px;
		line-height: 1.6;
	}

	.apply-email a {
		color: #409EFF;
		text-decoration: none;
		font-weight: bold;
	}

	.apply-email a:hover {
		text-decoration: underline;
	}

	.form-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
		gap: 20px;
		margin-bottom: 20px;
	}

	.form-group {
		display: flex;
		flex-direction: column;
	}

	.form-group.full-width {
		grid-column: 1 / -1;
	}

	.form-group label {
		font-weight: 600;
		margin-bottom: 8px;
		color: #666;
		font-size: 0.9rem;
	}

	input,
	textarea {
		padding: 10px 15px;
		border: 1px solid #ddd;
		border-radius: 4px;
		font-size: 1rem;
		transition: border-color 0.3s;
	}

	input:focus,
	textarea:focus {
		outline: none;
		border-color: #4a6bff;
	}

	textarea {
		resize: vertical;
		min-height: 100px;
	}

	.file-upload {
		position: relative;
		overflow: hidden;
		display: inline-block;
	}

	.file-upload input[type="file"] {
		position: absolute;
		left: 0;
		top: 0;
		opacity: 0;
		width: 100%;
		height: 100%;
		cursor: pointer;
	}

	.upload-label {
		display: flex;
		align-items: center;
		padding: 10px 15px;
		background: white;
		border: 1px dashed #ddd;
		border-radius: 4px;
		cursor: pointer;
		transition: border-color 0.3s;
	}

	.upload-label i {
		margin-right: 10px;
		color: #4a6bff;
	}

	.upload-label:hover {
		border-color: #4a6bff;
	}

	.file-hint {
		font-size: 0.8rem;
		color: #666;
		margin-top: 5px;
	}

	.submit-btn {
		width: 100%;
		padding: 12px;
		background: #4a6bff;
		color: white;
		border: none;
		border-radius: 4px;
		font-size: 1rem;
		cursor: pointer;
		transition: background 0.3s;
	}

	.submit-btn:hover:not(:disabled) {
		background: #3a5bef;
	}

	.submit-btn:disabled {
		background: #f5f5f5;
		cursor: not-allowed;
	}

	/* Modal Transition */
	.modal-enter-active,
	.modal-leave-active {
		transition: opacity 0.3s;
	}

	.modal-enter-active .modal-container,
	.modal-leave-active .modal-container {
		transition: transform 0.3s, opacity 0.3s;
	}

	.modal-enter,
	.modal-leave-to {
		opacity: 0;
	}

	.modal-enter .modal-container,
	.modal-leave-to .modal-container {
		transform: translateY(-20px);
		opacity: 0;
	}

	/* Responsive Adjustments */
	@media (max-width: 768px) {
		.hero-section {
			padding: 40px 20px;
		}

		.hero-section h1 {
			font-size: 2rem;
		}

		.filter-grid {
			grid-template-columns: 1fr !important;
		}

		.filter-actions {
			flex-direction: column;
			width: 100%;
		}

		.search-btn,
		.reset-filters {
			width: 100%;
			justify-content: center;
		}

		.job-list {
			grid-template-columns: 1fr !important;
		}

		.modal-container {
			width: 95% !important;
		}
	}
</style>